import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { environment } from '../../environments/environment';
import { LeakFormDetails, SteamCost, EditSteamCost } from './site.interfaces';
import { forkJoin } from 'rxjs';
import {
	EditFitting,
	EditFolder,
	EditHearingProtectionType,
	EditInsulationType,
	EditIsoNotVerifiedReason,
	EditStatus,
	EditTeam,
	EditVendor,
	NewFitting,
	NewFolder,
	NewHearingProtectionType,
	NewInsulationType,
	NewIsoNotVerifiedReason,
	NewStatus,
	NewTeam,
	NewVendor,
} from './site-settings.interfaces';

@Injectable({
	providedIn: 'root',
})
export class SiteService {
	private siteUrl = environment.apiUrl + 'Sites'; // URL to web api

	constructor(private http: HttpClient) {}

	getSite(id: string) {
		const url = `${this.siteUrl}/${id}`;
		return this.http.get(url);
	}

	getSiteNames() {
		const url = `${this.siteUrl}/MySiteNames`;
		return this.http.get(url);
	}

	getLeakFormData(siteId: string) {
		const url = `${environment.apiUrl}LeakFormDetails/FromSite/${siteId}`;
		return this.http.get<LeakFormDetails>(url);
	}

	getSteamCosts(id: string) {
		const url = `${environment.apiUrl}SteamCosts/FromSite/${id}`;
		return this.http.get(url);
	}

	updateSteamCost(steamCost: EditSteamCost) {
		const url = `${environment.apiUrl}SteamCosts/${steamCost.id}`;
		return this.http.put(url, steamCost);
	}

	updateSteamCosts(steamCosts: EditSteamCost[]) {
		const url = `${environment.apiUrl}SteamCosts`;
		return this.http.put(url, steamCosts);
	}

	addFitting(newFitting: NewFitting) {
		const url = `${environment.apiUrl}Fittings`;
		return this.http.post(url, newFitting);
	}

	updateFitting(editFitting: EditFitting) {
		const url = `${environment.apiUrl}Fittings`;
		return this.http.put(url, editFitting);
	}

	deleteFitting(fittingId: string) {
		const url = `${environment.apiUrl}Fittings/${fittingId}`;
		return this.http.delete(url);
	}

	addFolder(newFolder: NewFolder) {
		const url = `${environment.apiUrl}Folders`;
		return this.http.post(url, newFolder);
	}

	updateFolder(editFolder: EditFolder) {
		const url = `${environment.apiUrl}Folders`;
		return this.http.put(url, editFolder);
	}

	deleteFolder(folderId: string) {
		const url = `${environment.apiUrl}Folders/${folderId}`;
		return this.http.delete(url);
	}

	addHearingProtectionType(newHearingProtectionType: NewHearingProtectionType) {
		const url = `${environment.apiUrl}HearingProtectionTypes`;
		return this.http.post(url, newHearingProtectionType);
	}

	updateHearingProtectionType(editHearingProtectionType: EditHearingProtectionType) {
		const url = `${environment.apiUrl}HearingProtectionTypes`;
		return this.http.put(url, editHearingProtectionType);
	}

	deleteHearingProtectionType(hearingProtectionTypeId: string) {
		const url = `${environment.apiUrl}HearingProtectionTypes/${hearingProtectionTypeId}`;
		return this.http.delete(url);
	}

	addInsulationType(newInsulationType: NewInsulationType) {
		const url = `${environment.apiUrl}InsulationTypes`;
		return this.http.post(url, newInsulationType);
	}

	updateInsulationType(editInsulationType: EditInsulationType) {
		const url = `${environment.apiUrl}InsulationTypes`;
		return this.http.put(url, editInsulationType);
	}

	deleteInsulationType(insulationTypeId: string) {
		const url = `${environment.apiUrl}InsulationTypes/${insulationTypeId}`;
		return this.http.delete(url);
	}

	addIsoNotVerifiedReason(newIsoNotVerifiedReason: NewIsoNotVerifiedReason) {
		const url = `${environment.apiUrl}IsolationNotVerifiedReasons`;
		return this.http.post(url, newIsoNotVerifiedReason);
	}

	updateIsoNotVerifiedReason(editIsoNotVerifiedReason: EditIsoNotVerifiedReason) {
		const url = `${environment.apiUrl}IsolationNotVerifiedReasons`;
		return this.http.put(url, editIsoNotVerifiedReason);
	}

	deleteIsoNotVerifiedReason(isoNotVerifiedReasonId: string) {
		const url = `${environment.apiUrl}IsolationNotVerifiedReasons/${isoNotVerifiedReasonId}`;
		return this.http.delete(url);
	}

	addStatus(newStatus: NewStatus) {
		const url = `${environment.apiUrl}Status`;
		return this.http.post(url, newStatus);
	}

	updateStatus(editStatus: EditStatus) {
		const url = `${environment.apiUrl}Status`;
		return this.http.put(url, editStatus);
	}

	deleteStatus(statusId: string) {
		const url = `${environment.apiUrl}Status/${statusId}`;
		return this.http.delete(url);
	}

	addTeam(newTeam: NewTeam) {
		const url = `${environment.apiUrl}Teams`;
		return this.http.post(url, newTeam);
	}

	updateTeam(editTeam: EditTeam) {
		const url = `${environment.apiUrl}Teams`;
		return this.http.put(url, editTeam);
	}

	deleteTeam(teamId: string) {
		const url = `${environment.apiUrl}Teams/${teamId}`;
		return this.http.delete(url);
	}

	addVendor(newVendor: NewVendor) {
		const url = `${environment.apiUrl}Vendors`;
		return this.http.post(url, newVendor);
	}

	updateVendor(editVendor: EditVendor) {
		const url = `${environment.apiUrl}Vendors`;
		return this.http.put(url, editVendor);
	}

	deleteVendor(vendorId: string) {
		const url = `${environment.apiUrl}Vendors/${vendorId}`;
		return this.http.delete(url);
	}

	formatBoundingPolygon(polygon) {
		if (!polygon) {
			return [];
		}
		const region = polygon.map(function (obj) {
			const coords = [];
			coords.push(obj.latitude);
			coords.push(obj.longitude);
			return coords;
		});

		return region;
	}
}
